import React, {useState, useRef, useMemo, useCallback, useEffect} from 'react';
import {
  View,
  TextInput,
  Text,
  StyleSheet,
  ViewStyle,
  TextStyle,
  NativeSyntheticEvent,
  TextInputFocusEventData,
} from 'react-native';
import colors from 'theme/Colors';
import {spacing, corner_radius} from 'theme/Units';
import {mergeDeepRight} from 'ramda';
import {nonDigitRegex} from 'constants/regex';
import {v4 as uuid} from 'uuid';

type sizeTypes = 'lg' | 'md' | 'sm';
type inputStateTypes = 'default' | 'disabled' | 'error' | 'focus';

type OtpInputProps = Readonly<{
  value: string;
  onBlur: (e: NativeSyntheticEvent<TextInputFocusEventData>) => void;
  length?: number;
  onChange: (otp: string) => void;
  error?: string;
  disabled?: boolean;
  size?: sizeTypes;
}>;

export function OtpInput(props: OtpInputProps) {
  const {
    value,
    length = 6,
    error,
    disabled,
    size = 'md',
    onBlur,
    onChange,
  } = props;

  const fillOTPArrayFromValue = useCallback(() => {
    return Array.from({length}, (_, i) => value[i] ?? '');
  }, [length, value]);

  const [otp, setOtp] = useState<string[]>(fillOTPArrayFromValue());
  const [inputState, setInputState] = useState<inputStateTypes>(
    disabled ? 'disabled' : 'default',
  );
  const inputsRef = useRef<Array<TextInput | null>>([]);

  useEffect(() => {
    setOtp(fillOTPArrayFromValue());
  }, [fillOTPArrayFromValue]);

  function handleChange(index: number, value: string) {
    const sanitizedValue = value.replace(nonDigitRegex, '').slice(-1);

    const newOtp = [...otp];
    newOtp[index] = sanitizedValue;
    setOtp(newOtp);

    if (sanitizedValue && index < length - 1) {
      inputsRef.current[index + 1]?.focus();
    }

    onChange(newOtp.join(''));
  }

  function handleKeyPress(index: number, key: string) {
    if (key === 'Backspace') {
      const newOtp = [...otp];
      if (otp[index] === '' && index > 0) {
        newOtp[index - 1] = ''; // Clear previous input
        setOtp(newOtp);

        inputsRef.current[index - 1]?.focus(); // Move focus back
      } else {
        newOtp[index] = ''; // Clear current input
        setOtp(newOtp);
      }
      onChange(newOtp.join(''));
    }
  }

  const updateInputState = useCallback(
    (state: inputStateTypes) => {
      if (disabled) {
        return;
      }
      setInputState(state);
    },
    [disabled],
  );

  function handleFocus(e: NativeSyntheticEvent<TextInputFocusEventData>) {
    updateInputState('focus');
  }

  function handleBlur(e: NativeSyntheticEvent<TextInputFocusEventData>) {
    updateInputState(error ? 'error' : 'default');
    onBlur?.(e);
  }

  useEffect(() => {
    if (error) {
      updateInputState('error');
    } else {
      updateInputState('default');
    }
  }, [error, updateInputState]);

  const styles = useMemo<styleType>(
    () =>
      mergeDeepRight(
        mergeDeepRight(baseStyles, inputStateStyle[inputState]),
        sizeStyles[size],
      ) as styleType,
    [inputState, size],
  );

  return (
    <View style={styles.container}>
      <View style={styles.inputWrapper}>
        {otp.map((digit, index) => {
          return (
            <TextInput
              style={styles.input}
              key={index}
              ref={ref => (inputsRef.current[index] = ref)}
              value={digit ? '●' : ''}
              onChangeText={text => handleChange(index, text)}
              onKeyPress={({nativeEvent}) =>
                handleKeyPress(index, nativeEvent.key)
              }
              onFocus={handleFocus}
              onBlur={handleBlur}
              keyboardType="numeric"
              maxLength={1}
              editable={!disabled}
            />
          );
        })}
      </View>
      {error && <Text style={styles.errorText}>{error}</Text>}
    </View>
  );
}

type styleType = {
  container: ViewStyle;
  inputWrapper: ViewStyle;
  input: TextStyle;
  errorText: TextStyle;
  label: TextStyle;
};

const baseStyles: styleType = StyleSheet.create({
  container: {
    alignItems: 'center',
  },
  inputWrapper: {
    flexDirection: 'row',
    gap: spacing[2],
  },
  input: {
    borderWidth: 2,
    borderRadius: corner_radius['rounded-lg'],
    borderColor: colors.palette.stroke,
    textAlign: 'center',
    fontSize: 30,
    fontWeight: '600',
    color: colors.palette.brandBlue,
    backgroundColor: colors.palette.white,
  },
  errorText: {
    color: colors.palette.errorRedDark,
    fontSize: 12,
    fontWeight: '400',
    marginTop: spacing[1],
  },
  label: {},
});

const sizeStyles: Record<sizeTypes, Partial<styleType>> = {
  lg: {
    input: {
      height: 48,
      width: 48,
      fontSize: 18,
    },
  },
  md: {
    input: {
      height: 40,
      width: 40,
      fontSize: 14,
    },
  },
  sm: {
    input: {
      height: 32,
      width: 32,
      fontSize: 14,
    },
  },
};

const inputStateStyle: Record<inputStateTypes, Partial<styleType>> = {
  default: {
    input: {
      borderColor: colors.palette.stroke,
    },
  },
  disabled: {
    input: {
      backgroundColor: 'rgba(160, 160, 160, 0.2)',
      borderColor: colors.palette.stroke,
    },
  },
  error: {
    input: {
      borderColor: colors.palette.errorRedDark,
    },
    label: {
      color: colors.palette.errorRedDark,
    },
  },
  focus: {
    input: {
      borderColor: 'rgba(158, 119, 237, 0.4)',
      borderWidth: 2,
    },
  },
};
